import MigiriLogo from "../../components/svg/MigiriLogo";

import styles from "./FreeTrial.module.css";
import CheckIcon from "../../components/svg/CheckIcon";
import clsx from "clsx";
import StartTrialButton from "./StartTrialButton";
import StripeLogo from "../../components/svg/StripeLogo";
import { useState, useEffect } from "react";

import {ExpressCheckoutElement, PaymentElement} from "@stripe/react-stripe-js";
import {SubscriptionData} from "../../lib/mappers/subscriptionData";
import {StripeExpressCheckoutElementOptions, StripePaymentElementOptions} from "@stripe/stripe-js";
import {ApplePayRecurringPaymentRequestIntervalUnit} from "@stripe/stripe-js/dist/stripe-js/elements/apple-pay";
import usePaymentAttempt, {
  successReturnUrl,
} from "../../hooks/usePaymentAttempt";
import { useRef } from "react";
import FreeTrialCard from "./FreeTrialCard";
import { scrollIntoView } from "./handlers";
import { useNavigate } from "react-router-dom";
import PaymentCard from "./PaymentCard";
import {
  getDateWithDaysOffset,
  getPeriodString,
} from "../../lib/helpers/dateHelper.ts";
import { captureMessage } from "../../lib/sentry.ts";

const stripeExpressCheckoutOptions = (subscriptionData: SubscriptionData) => {
  if(subscriptionData.isPaymentRequired()) {
    const stripeExpressCheckoutElementOptions: StripeExpressCheckoutElementOptions = {
      paymentMethodOrder: ['apple_pay', 'google_pay'],
      buttonTheme: {
        applePay: "white",
        googlePay: "white",
      },
      buttonType: {
        applePay: 'plain',
        googlePay: 'plain',
      },
      paymentMethods: {
        applePay: 'auto',
        googlePay: 'auto',
        link: 'never'
      },
    };
    return stripeExpressCheckoutElementOptions;
  } else {
    const now = new Date();
    const paymentStart = getDateWithDaysOffset(
        now,
        subscriptionData.getDaysAmount()
    );
    const paymentAmount = subscriptionData.getPaymentData().amount * 100;
    const stripeExpressCheckoutElementOptions: StripeExpressCheckoutElementOptions = {
      buttonTheme: {
        applePay: "white",
        googlePay: "white",
      },
      buttonType: {
        applePay: 'plain',
        googlePay: 'plain',
      },
      paymentMethods: {
        applePay: 'auto',
        googlePay: 'auto',
        link: 'never'
      },
      applePay: {
        recurringPaymentRequest : {
            paymentDescription: "Migiri Subscription",
            managementURL: "https://www.migiri.app/about/en/",
            regularBilling: {
                label: "Migiri Subscription",
                amount: paymentAmount,
                recurringPaymentStartDate: paymentStart,
                recurringPaymentIntervalUnit: getPeriodString(
                    subscriptionData.getPaymentData().period
                )?.singular as ApplePayRecurringPaymentRequestIntervalUnit,
            },
            trialBilling: {
                label: "Trial",
                amount: 0,
                recurringPaymentStartDate: now,
                recurringPaymentEndDate: paymentStart,
                recurringPaymentIntervalUnit: "day",
            },
        }
      }
    };
    return stripeExpressCheckoutElementOptions;
  }

};

const stripePaymentOptions = () => {
    const stripePaymentElementOptionsForSubscription: StripePaymentElementOptions = {
      paymentMethodOrder: ['card'],
      wallets: {
        applePay: 'never',
        googlePay: 'never',
      },
    };
    return stripePaymentElementOptionsForSubscription;
};

const FreeTrial = ({
  freeTrialActive,
  subscriptionData,
}: {
  freeTrialActive: boolean;
  subscriptionData: SubscriptionData;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { setupAttemptHandler, paymentAttemptHandler } = usePaymentAttempt();
  const [expressCheckoutMounted, setExpressCheckoutMounted] = useState(false);
  const [expressCheckoutVisible, setExpressCheckoutVisible] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1000);

  useEffect(() => {
    if (expressCheckoutMounted) {
        setExpressCheckoutVisible(true);
    }
  }, [expressCheckoutMounted]);


  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const submitHandler = subscriptionData.isPaymentRequired()
    ? paymentAttemptHandler
    : setupAttemptHandler;
  const stripePaymentElementOptions = stripePaymentOptions();

  const stripeExpressCheckoutElementOptions = stripeExpressCheckoutOptions(subscriptionData);

  const expressCheckoutElement = <ExpressCheckoutElement
    options={stripeExpressCheckoutElementOptions}
    onReady={(event) => {
      // If payment methods are available, set expressCheckoutMounted to true
      if (event.availablePaymentMethods) {
        setExpressCheckoutMounted(true);
      }
    }}
    onConfirm={async () => {
      const error = await submitHandler();
      if (error === undefined) {
        navigate(successReturnUrl);
      } else {
        captureMessage(`ExpressCheckout submitHandler ${error}`)
      }
    }}
  />

  return (
    <>
      <div className="slanted-reflection-wrapper">
        <div className="slanted-reflection" />
      </div>
      <div className={clsx("free-trial-content", styles.content)}>
        <div className={styles.contentWrapper}>
          <div className={styles.infoSection}>
            <div className={styles.migiriLogo}>
              <MigiriLogo />
            </div>
            <h2 className="semibold">
              {freeTrialActive
                ? "Your first week is on us"
                : "Give yourself the chance to transform your life"}
            </h2>

            <p className={clsx("caption-sm", styles.captionMigiriIncludes)}>
              THIS IS WHAT MIGIRI INCLUDES:
            </p>
            <ul>
              <li>
                <CheckIcon className={styles.listIcon} />A powerful porn blocker
                with the ability to block custom sites
              </li>
              <li>
                <CheckIcon className={styles.listIcon} />A progress counter with
                awards for achieving milestones
              </li>
              <li>
                <CheckIcon className={styles.listIcon} />
                Guidance and tools to avoid relapsing
              </li>
              <li>
                <CheckIcon className={styles.listIcon} />
                Access to a growing library of content resources - from
                meditations to workouts and more!
              </li>
              <li>
                <CheckIcon className={styles.listIcon} />
                The option to add an accountability partner to support your
                journey
              </li>
              <li>
                <CheckIcon className={styles.listIcon} />A single subscription
                covers all your Apple devices
              </li>
            </ul>

            {!isDesktop && subscriptionData.isPaymentRequired() && (
              <PaymentCard paymentData={subscriptionData.getPaymentData()} />
            )}
            {!isDesktop && !subscriptionData.isPaymentRequired() && (
              <FreeTrialCard
                freeDays={subscriptionData.getDaysAmount()}
                paymentData={subscriptionData.getPaymentData()}
                freeTrialActive={freeTrialActive}
              />
            )}

            {!isDesktop &&
              <div className={clsx(styles.paymentButtonWrapper, expressCheckoutVisible && styles.visible)}>
                {expressCheckoutElement}
              </div>
            }

            {!isDesktop && expressCheckoutMounted && (
              <div className={styles.divider}>or try with card</div>
            )}
          </div>

          <div className={styles.paymentSection} ref={ref}>
            {isDesktop && subscriptionData.isPaymentRequired() && (
              <PaymentCard paymentData={subscriptionData.getPaymentData()} />
            )}

            {isDesktop && !subscriptionData.isPaymentRequired() && (
              <FreeTrialCard
                freeDays={subscriptionData.getDaysAmount()}
                paymentData={subscriptionData.getPaymentData()}
                freeTrialActive={freeTrialActive}
              />
            )}

            {isDesktop && expressCheckoutElement}

            {isDesktop && expressCheckoutMounted && (
              <div className={styles.divider}>or try with card</div>
            )}

            <form onSubmit={submitHandler} style={{ overflow: "hidden" }}>
              <div className={styles.formWrapper}>
                <PaymentElement options={stripePaymentElementOptions} />

                <StartTrialButton
                  freeDays={
                    subscriptionData.isPaymentRequired()
                      ? 0
                      : subscriptionData.getDaysAmount()
                  }
                  freeTrialActive={freeTrialActive}
                  onClick={async (e) => {
                    e.preventDefault();
                    const error = await submitHandler();
                    if (error !== undefined) {
                      scrollIntoView(ref.current!);
                    } else {
                      navigate(successReturnUrl);
                    }
                  }}
                />

                <p className={clsx(styles.securedByStripe, "caption-sm")} >
                  Secured by&nbsp;
                  <StripeLogo />
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default FreeTrial;
